
import { useState, useEffect, useCallback, useRef } from "react";
import { StandardButton } from "@/components/ui/standard-button";
import BookSearchSection from "./BookSearchSection";
import StatusSection from "./StatusSection";
import ConceptualTagsSection from "./ConceptualTagsSection";
import PersonalResonanceSection from "./PersonalResonanceSection";
import NotesSection from "./NotesSection";
import PublisherResonanceBadge from "../PublisherResonanceBadge";
import { EnhancedBookSuggestion } from "@/services/googleBooksApi";
import { findMatchingPublisherSeries, PublisherSeries } from "@/services/publisherService";
import { useToast } from "@/hooks/use-toast";
import { useHapticFeedback } from "@/hooks/useHapticFeedback";
import { useSwipeGesture } from "@/hooks/useSwipeGesture";
import { z } from "zod";
import StarRating from "@/components/StarRating";

// Local form validation schema matching BookFormData
const formSchema = z.object({
  title: z.string().trim().min(1, "Title is required"),
  author: z.string().trim().min(1, "Author is required"),
  status: z.enum(["reading", "read", "want-to-read"]),
  tags: z.array(z.string()).max(50).optional().default([]),
  notes: z.string().max(5000).optional().or(z.literal("")),
  cover_url: z.string().max(2048).optional().or(z.literal("")).refine(
    (val) => !val || val === "" || val.startsWith("http://") || val.startsWith("https://"),
    { message: "Cover URL must be a valid URL" }
  ),
  rating: z.object({
    truth: z.boolean(),
    confirmed: z.boolean(),
    disrupted: z.boolean(),
    rewired: z.boolean(),
  }),
  star_rating: z.number().int().min(1).max(5).optional(),
  publisher_series_id: z.string().uuid().optional().or(z.literal("")).or(z.undefined()),
});
interface BookFormData {
  title: string;
  author: string;
  status: string;
  tags: string[];
  notes: string;
  cover_url: string;
  rating: {
    truth: boolean;
    confirmed: boolean;
    disrupted: boolean;
    rewired: boolean;
  };
  star_rating?: number;
  publisher_series_id?: string;
}

interface BookFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (book: BookFormData) => void | Promise<void>;
  editingBook?: any;
}

const BookFormModal = ({ isOpen, onClose, onSubmit, editingBook }: BookFormModalProps) => {
  const { toast } = useToast();
  const { impact, notification } = useHapticFeedback();
  const wasOpen = useRef(false);
  
  // Swipe down to close
  const { handlers: swipeHandlers } = useSwipeGesture(
    { onSwipeDown: onClose },
    { direction: 'down', threshold: 80 }
  );
  
  const [formData, setFormData] = useState<BookFormData>({
    title: "",
    author: "",
    status: "want-to-read",
    tags: [],
    notes: "",
    cover_url: "",
    rating: { truth: false, confirmed: false, disrupted: false, rewired: false },
    star_rating: undefined,
    publisher_series_id: undefined
  });

  const [titleSearch, setTitleSearch] = useState("");
  /** Blurb and categories from the chosen search result, kept only to inform
   *  conceptual-node suggestion. Not part of formData: none of it is saved. */
  const [selectedBookMeta, setSelectedBookMeta] = useState<{
    description?: string;
    categories?: string[];
  }>({});
  const [authorSearch, setAuthorSearch] = useState("");
  const [detectedSeries, setDetectedSeries] = useState<PublisherSeries | null>(null);
  const [selectedAuthorName, setSelectedAuthorName] = useState<string>("");

  // Haptic feedback on open/close
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      impact.medium();
    } else if (!isOpen && wasOpen.current) {
      impact.light();
    }
    wasOpen.current = isOpen;
  }, [isOpen, impact]);

  const resetForm = useCallback(() => {
    setFormData({
      title: "",
      author: "",
      status: "want-to-read",
      tags: [],
      notes: "",
      cover_url: "",
      rating: { truth: false, confirmed: false, disrupted: false, rewired: false },
      star_rating: undefined,
      publisher_series_id: undefined
    });
    setTitleSearch("");
    setAuthorSearch("");
    setDetectedSeries(null);
    // Otherwise the next book logged would be described by the previous one,
    // and node suggestion would quietly key off the wrong blurb.
    setSelectedBookMeta({});
  }, []);

  useEffect(() => {
    if (editingBook) {
      setFormData({
        title: editingBook.title || "",
        author: editingBook.author || "",
        status: editingBook.status || "want-to-read",
        tags: editingBook.tags || [],
        notes: editingBook.notes || "",
        cover_url: editingBook.cover_url || "",
        rating: {
          truth: editingBook.rating?.truth || false,
          confirmed: editingBook.rating?.confirmed || false,
          disrupted: editingBook.rating?.disrupted || false,
          rewired: editingBook.rating?.rewired || false
        },
        star_rating: editingBook.star_rating ?? undefined,
        publisher_series_id: editingBook.publisher_series_id
      });
      setTitleSearch(editingBook.title || "");
      setAuthorSearch(editingBook.author || "");
      if (editingBook.publisher_series) {
        setDetectedSeries(editingBook.publisher_series);
      }
    } else {
      resetForm();
    }
  }, [editingBook, isOpen, resetForm]);

  useEffect(() => {
    const detectPublisherSeries = async () => {
      if (formData.title && formData.author && !editingBook) {
        try {
          const series = await findMatchingPublisherSeries(formData.title, formData.author);
          if (series) {
            setDetectedSeries(series);
            setFormData(prev => ({ ...prev, publisher_series_id: series.id }));
          } else {
            setDetectedSeries(null);
            setFormData(prev => ({ ...prev, publisher_series_id: undefined }));
          }
        } catch (error) {
          console.error('Error detecting publisher series:', error);
        }
      }
    };

    const timeoutId = setTimeout(detectPublisherSeries, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.title, formData.author, editingBook]);

  const handleBookSelect = useCallback((book: EnhancedBookSuggestion) => {
    setFormData(prev => ({
      ...prev,
      title: book.title,
      author: book.author,
      cover_url: book.coverUrl || ""
    }));
    setTitleSearch(book.title);
    setAuthorSearch(book.author);
    // The search already returns the blurb and the provider's categories —
    // BookSearchSection passes the whole EnhancedBookSuggestion up. This used to
    // be typed as the narrower BookSuggestion and read three fields, so the
    // richest signal about what a book actually IS was dropped on the floor and
    // node suggestion had nothing but title and author to work from.
    setSelectedBookMeta({
      description: book.description,
      categories: book.categories,
    });
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Validate using Zod schema
      formSchema.parse(formData);

      // Call onSubmit and wait for completion
      await Promise.resolve(onSubmit(formData));
      
      // Success haptic
      notification.success();
      
      // Reset form and close modal after successful submission
      resetForm();
      onClose();
    } catch (error) {
      // Error haptic
      notification.error();
      
      if (error instanceof z.ZodError) {
        const firstError = error.errors[0];
        toast({
          title: "Validation Error",
          description: `${firstError.path.join('.')}: ${firstError.message}`,
          variant: "destructive",
        });
      } else {
        console.error('BookForm submission failed:', error);
        toast({
          title: "Submission Error",
          description: "Failed to save changes. Please try again.",
          variant: "destructive",
        });
      }
      // Keep modal open so user can correct issues
    }
  }, [formData, onSubmit, onClose, toast, resetForm, notification]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center p-4 pt-20 sm:pt-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-slate-800 border border-slate-600 rounded-lg w-full max-w-2xl max-h-[85vh] sm:max-h-[90vh] overflow-y-auto scrollbar-hide">
        <div 
          className="flex items-center justify-center p-4 sm:p-6 border-b border-slate-700 cursor-grab active:cursor-grabbing"
          {...swipeHandlers}
        >
          <div className="w-10 h-1 bg-slate-500 rounded-full mb-2 sm:hidden" />
          <h2 className="text-slate-200 text-lg font-medium w-full text-center sm:text-left">
            {editingBook ? "Edit Signal" : "Log Signal"}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <BookSearchSection
            titleSearch={titleSearch}
            authorSearch={authorSearch}
            coverUrl={formData.cover_url}
            title={formData.title}
            onTitleSearchChange={(value) => {
              setTitleSearch(value);
              setFormData(prev => ({ ...prev, title: value }));
            }}
            onAuthorSearchChange={(value) => {
              setAuthorSearch(value);
              setFormData(prev => ({ ...prev, author: value }));
              // Clear selected author if user manually changes the field
              if (value !== selectedAuthorName) {
                setSelectedAuthorName("");
              }
            }}
            onBookSelect={handleBookSelect}
            onAuthorSelect={(author) => {
              setFormData(prev => ({ ...prev, author: author.name }));
              setAuthorSearch(author.name);
              setSelectedAuthorName(author.name); // Set the selected author for filtering
            }}
            selectedAuthorName={selectedAuthorName}
            isEditMode={!!editingBook}
          />
          
          {detectedSeries && (
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-purple-300 text-sm font-medium">Publisher Resonance Detected</span>
              </div>
              <PublisherResonanceBadge series={detectedSeries} />
              <p className="text-slate-400 text-xs mt-2">{detectedSeries.description}</p>
            </div>
          )}
          
          <StatusSection
            status={formData.status}
            onStatusChange={(status) => setFormData(prev => ({ ...prev, status }))}
          />

          <div>
            <label className="text-slate-300 text-sm">Your rating</label>
            <div className="mt-2 flex items-center gap-3">
              <StarRating
                size="md"
                value={formData.star_rating}
                onChange={(v) => setFormData(prev => ({ ...prev, star_rating: v }))}
                ariaLabel="Your rating, 1 to 5 stars"
              />
              {formData.star_rating ? (
                <span className="text-xs text-slate-400">{formData.star_rating}/5 — tap selected star to clear</span>
              ) : (
                <span className="text-xs text-slate-500">Optional</span>
              )}
            </div>
          </div>

          <ConceptualTagsSection
            selectedTags={formData.tags}
            onTagToggle={(tag) => setFormData(prev => ({
              ...prev,
              tags: prev.tags.includes(tag)
                ? prev.tags.filter(t => t !== tag)
                : [...prev.tags, tag]
            }))}
            bookTitle={formData.title}
            bookAuthor={formData.author}
            /* Was `titleSearch` — the raw contents of the search box, i.e. the
               title typed back at it. Suggestion had no real description to
               work from and the AI was being handed the query as the blurb. */
            bookDescription={selectedBookMeta.description}
            bookCategories={selectedBookMeta.categories}
            isEditMode={!!editingBook}
          />
          
          <PersonalResonanceSection
            rating={formData.rating}
            onResonanceChange={(key, checked) => {
              const selectedCount = Object.values(formData.rating).filter(Boolean).length;
              if (checked && selectedCount >= 2) return;
              
              setFormData(prev => ({
                ...prev,
                rating: { ...prev.rating, [key]: checked }
              }));
            }}
          />
          
          <NotesSection
            notes={formData.notes}
            onNotesChange={(notes) => setFormData(prev => ({ ...prev, notes }))}
          />
          
          <div className="flex justify-end space-x-3 pt-4">
            <StandardButton
              type="button"
              variant="standard"
              onClick={onClose}
              className="touch-manipulation"
            >
              Cancel
            </StandardButton>
            <StandardButton
              type="submit"
              variant="standard"
              className="touch-manipulation active:scale-95"
            >
              {editingBook ? "Update Signal" : "Log Signal"}
            </StandardButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookFormModal;
